import { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { api } from '../lib/api';
import { ScrollText, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * YONO-style legal terms modal — owner MUST scroll to bottom + tick consent.
 * `onAccepted` is called once the user successfully accepts. `open` is controlled.
 */
export default function TermsModal({ open, onOpenChange, onAccepted }) {
  const [terms, setTerms] = useState(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setScrolledToEnd(false);
    setAgreed(false);
    api.get('/owner/terms').then(r => setTerms(r.data)).catch(() => {});
  }, [open]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      setScrolledToEnd(true);
    }
  };

  const accept = async () => {
    setBusy(true);
    try {
      const r = await api.post('/owner/terms/accept');
      toast.success(`Agreement accepted (${r.data.version})`);
      onAccepted?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record acceptance');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="terms-modal" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ScrollText className="w-5 h-5 text-[#D35400]"/>Seller Agreement</DialogTitle>
        </DialogHeader>
        {!terms ? (
          <div className="py-10 text-center text-sm text-ink/60 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading agreement…</div>
        ) : (
          <>
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
              <span>Please <b>scroll all the way to the bottom</b> and tick the box below to confirm. Your acceptance is electronically logged with timestamp + IP under Section 10A of the IT Act, 2000.</span>
            </div>
            <div
              ref={bodyRef}
              data-testid="terms-body"
              onScroll={onScroll}
              className="mt-3 max-h-[55vh] overflow-y-auto rounded-md border border-border bg-[#FBF8F1] p-4 text-[13px] leading-relaxed text-ink whitespace-pre-wrap font-mono"
            >
              {terms.text}
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 text-xs text-ink/60">
              <span>Version: <b>{terms.version}</b></span>
              <span data-testid="terms-scroll-status" className={scrolledToEnd ? 'text-[#1B5E20] font-semibold' : 'text-ink/50'}>
                {scrolledToEnd ? '✓ You have read to the end' : 'Keep scrolling to the bottom…'}
              </span>
            </div>
            <label className="mt-3 flex items-start gap-2 text-sm" data-testid="terms-agree-label">
              <input
                data-testid="terms-agree-check"
                type="checkbox"
                disabled={!scrolledToEnd}
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#E67E22] disabled:opacity-40"
              />
              <span className={!scrolledToEnd ? 'text-ink/40' : ''}>
                I have read, understood, and agree to the BharatCart Seller Agreement (Version {terms.version}). I am at least 18 years old and authorised to register this business.
              </span>
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button data-testid="terms-cancel-btn" type="button" onClick={() => onOpenChange(false)} className="rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-muted">Cancel</button>
              <button
                data-testid="terms-accept-btn"
                type="button"
                disabled={!scrolledToEnd || !agreed || busy}
                onClick={accept}
                className="inline-flex items-center gap-2 rounded-full bg-[#D35400] hover:bg-[#B84300] disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 py-2 text-sm font-semibold"
              >
                {busy ? <><Loader2 className="w-4 h-4 animate-spin"/>Recording…</> : <><Check className="w-4 h-4"/>I have read and agree</>}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
